import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Users, Eye, ChevronRight, Search } from "lucide-react";

export function RepTracker({ members, getScoreColor, getScoreBg, getClassBadge, getTrendIcon }) {
  const [selectedRep, setSelectedRep] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.team_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Card className="premium-card mb-8" data-testid="rep-tracker">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-bold text-[#0B1F3B]">Rep Readiness Tracker</CardTitle>
              <CardDescription className="text-slate-400">{filteredMembers.length} advisors</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text" placeholder="Search reps..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#2563EB]"
                data-testid="rep-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rep</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Team</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Readiness</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sessions</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Strongest</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Gap</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Practice</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Trend</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((rep) => {
                  const cls = getClassBadge(rep.classification);
                  const ClsIcon = cls.icon;
                  return (
                    <tr key={rep.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`rep-row-${rep.id}`}>
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium text-[#0B1F3B]">{rep.name}</p>
                        <p className="text-xs text-slate-400">{rep.email}</p>
                      </td>
                      <td className="py-3 px-4"><span className="text-sm text-slate-500">{rep.team_name || "Unassigned"}</span></td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className={`text-sm font-bold ${getScoreColor(rep.average_readiness)}`}>
                            {rep.average_readiness > 0 ? `${rep.average_readiness}%` : "\u2014"}
                          </span>
                          <Badge className={`${cls.bg} text-xs px-1.5 py-0.5`}><ClsIcon className="w-3 h-3" /></Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center text-sm font-semibold text-[#0B1F3B]">{rep.total_sessions}</td>
                      <td className="py-3 px-4"><span className="text-xs text-[#14B8A6] font-medium">{rep.strongest_skill}</span></td>
                      <td className="py-3 px-4"><span className="text-xs text-red-500 font-medium">{rep.biggest_gap}</span></td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-slate-400">
                          {rep.last_practice ? new Date(rep.last_practice).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : "Never"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">{getTrendIcon(rep.trend)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-[#2563EB] hover:text-[#2563EB] hover:bg-[#2563EB]/5"
                            onClick={() => setSelectedRep(rep)} data-testid={`view-rep-${rep.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                          <Link to={`/profile/${rep.id}`}>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-[#0B1F3B]">
                              Profile <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredMembers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No reps found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Side Panel */}
      <Sheet open={!!selectedRep} onOpenChange={(open) => { if (!open) setSelectedRep(null); }}>
        <SheetContent className="w-[440px] sm:max-w-[440px] overflow-y-auto" aria-describedby="rep-detail-desc">
          {selectedRep && (
            <>
              <SheetHeader className="mb-6">
                <SheetTitle className="text-[#0B1F3B]">{selectedRep.name}</SheetTitle>
                <p id="rep-detail-desc" className="text-sm text-slate-400">{selectedRep.email}</p>
              </SheetHeader>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl text-center">
                    <p className={`text-2xl font-bold ${getScoreColor(selectedRep.average_readiness)}`}>{selectedRep.average_readiness || 0}%</p>
                    <p className="text-xs text-slate-500">Readiness</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-[#0B1F3B]">{selectedRep.total_sessions}</p>
                    <p className="text-xs text-slate-500">Sessions</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`${getClassBadge(selectedRep.classification).bg} px-3 py-1`}>{getClassBadge(selectedRep.classification).label}</Badge>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    {getTrendIcon(selectedRep.trend)}
                    <span className="capitalize">{selectedRep.trend}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Team", value: selectedRep.team_name || "Unassigned", cls: "text-[#0B1F3B]" },
                    { label: "Strongest Skill", value: selectedRep.strongest_skill, cls: "text-[#14B8A6]" },
                    { label: "Biggest Gap", value: selectedRep.biggest_gap, cls: "text-red-500" },
                    { label: "Last Practice", value: selectedRep.last_practice ? new Date(selectedRep.last_practice).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : "Never", cls: "text-slate-600" },
                  ].map(({ label, value, cls }) => (
                    <div key={label} className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">{label}</span>
                      <span className={`text-sm font-medium ${cls}`}>{value}</span>
                    </div>
                  ))}
                </div>
                {selectedRep.competencies && (
                  <div>
                    <h4 className="text-sm font-bold text-[#0B1F3B] mb-3">Competency Breakdown</h4>
                    <div className="space-y-2.5">
                      {Object.entries(selectedRep.competencies).map(([key, val]) => (
                        <div key={key}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-600">{key}</span>
                            <span className={`text-xs font-bold ${getScoreColor(val)}`}>{val}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${getScoreBg(val)}`} style={{ width: `${val}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Link to={`/profile/${selectedRep.id}`} className="block pt-2">
                  <Button className="w-full bg-[#0B1F3B] hover:bg-[#0B1F3B]/90"><Eye className="w-4 h-4 mr-2" /> View Full Profile</Button>
                </Link>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
